// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file usePopover.tsx
 * @input Uses useLayer, useFocusTrap, React hooks
 * @output Exports usePopover hook for popover dialogs with focus trapping
 * @position Higher-level layer utility; used by DatePicker, Combobox, etc.
 *
 * Combines popover layer behavior with focus trap for dialog-like popovers.
 * Use this for interactive popover content that should trap focus.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Popover/index.ts
 */
import React, { useCallback, useEffect, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useLayer } from "../Layer/useLayer.js";
import { useFocusTrap } from "../hooks/useFocusTrap.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, shadowVars } from "../theme/tokens.stylex.js";
import { Button } from "../Button/index.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  surface: {
    kWkggS: "x1prclbq",
    kaIpWk: "x1hviunn",
    kGVxlE: "x1i5ehqx",
    $$css: true
  },
  contentWrapper: {
    kVAEAm: "x1n2onr6",
    $$css: true
  }
};

/**
 * Options for usePopover
 */

/**
 * Return type for usePopover
 */

/**
 * Hook for creating popover dialogs with focus trapping.
 *
 * Combines:
 * - `useLayer` for popover positioning using CSS anchor positioning
 * - `useFocusTrap` for trapping focus within the popover content
 * - Auto-focus first element on open
 * - Escape key to close (configurable via hasEscapeDismiss)
 * - Hidden close button that reveals on focus for accessibility
 *
 * The render function automatically wraps your content in a focus trap container
 * and appends a hidden close button. The button appears at the end of the popover,
 * is visually hidden until focused, then shows a tooltip-like message (default: "Close popover").
 *
 * @example
 * ```
 * function DatePickerExample() {
 *   const inputRef = useRef<HTMLInputElement>(null);
 *   const popover = usePopover({
 *     onHide: () => inputRef.current?.focus(),
 *     closeButtonLabel: 'Close calendar',
 *   });
 *   return (
 *     <>
 *       <input ref={inputRef} />
 *       <button
 *         ref={popover.triggerRef}
 *         onClick={popover.toggle}
 *         {...popover.triggerProps}>
 *         Open Calendar
 *       </button>
 *       {popover.render(
 *         <Calendar />,
 *         { placement: 'below', alignment: 'start' }
 *       )}
 *     </>
 *   );
 * }
 * ```
 */
export function usePopover(options = {}) {
  const {
    onShow,
    onHide,
    xstyle,
    hasLightDismiss = true,
    hasEscapeDismiss = true,
    hasAutoFocus = true,
    hasSurface = true,
    hasCloseButton = true,
    closeButtonLabel: closeButtonLabelFromProps,
    dialogLabel,
    role = 'dialog',
    isModal = true
  } = options;
  const t = useTranslator();
  const closeButtonLabel = closeButtonLabelFromProps ?? t('@astryx.popover.close');

  // Track the trigger element for returning focus
  const triggerElementRef = useRef(null);

  // Track whether to skip auto-focus for the current open event
  const skipAutoFocusRef = useRef(false);

  // Core layer for popover positioning
  const layer = useLayer({
    mode: 'context',
    lightDismiss: hasLightDismiss,
    onShow,
    onHide
  });

  // Focus trap for the popover content. Escape stays registered while light
  // dismiss is on (native popover="auto" closes on Escape regardless), so a
  // host Dialog keeps deferring to this trap instead of double-dismissing.
  const {
    containerRef: contentRef,
    focusFirst
  } = useFocusTrap({
    isActive: layer.isOpen,
    onEscape: hasEscapeDismiss || hasLightDismiss ? layer.hide : undefined
  });

  // Auto-focus first element when popover opens (unless skipped)
  useEffect(() => {
    if (layer.isOpen && hasAutoFocus && !skipAutoFocusRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        focusFirst();
      });
    }
    // Reset the skip flag after the effect runs
    if (!layer.isOpen) {
      skipAutoFocusRef.current = false;
    }
  }, [layer.isOpen, hasAutoFocus, focusFirst]);

  // Combined ref for trigger element (layer anchor + our ref)
  const triggerRef = useCallback(el => {
    triggerElementRef.current = el;
    layer.ref(el);
  }, [layer]);

  // Show function with optional skipAutoFocus
  const show = useCallback(showOptions => {
    skipAutoFocusRef.current = showOptions?.skipAutoFocus ?? false;
    layer.show();
  }, [layer]);

  // Toggle function
  const toggle = useCallback(() => {
    if (layer.isOpen) {
      layer.hide();
    } else {
      show();
    }
  }, [layer, show]);

  // ARIA attributes for the trigger
  const triggerProps = {
    'aria-haspopup': role === 'dialog' ? 'dialog' : 'true',
    'aria-expanded': layer.isOpen,
    'aria-controls': layer.id
  };

  // Dev-time guardrail: a dialog popover should always be labeled. Warn once
  // per hook instance (in an effect) rather than on every render.
  const warnedUnnamedDialogRef = useRef(false);
  useEffect(() => {
    if (role === 'dialog' && !dialogLabel && !warnedUnnamedDialogRef.current) {
      warnedUnnamedDialogRef.current = true;
      console.warn('usePopover: role="dialog" without a `dialogLabel` renders an unnamed ' + 'dialog. Pass `dialogLabel`, or use `role: "none"` for listbox/menu ' + 'popups whose content already carries its own role.');
    }
  }, [role, dialogLabel]);

  // Wrapped render function that includes surface styles and optional hidden close button
  const render = useCallback((children, props) => {
    return layer.render(/*#__PURE__*/_jsxs("div", {
      ref: contentRef,
      role: role === 'dialog' ? 'dialog' : undefined,
      "aria-modal": role === 'dialog' && isModal ? true : undefined,
      "aria-label": role === 'dialog' ? dialogLabel : undefined,
      ...stylex.props(styles.contentWrapper, hasSurface && styles.surface, xstyle),
      children: [children, hasCloseButton && /*#__PURE__*/_jsx("div", {
        ...{
          className: "x10l6tqk x1ey2m1c x1nrll8i x31h1t8 x1vjfegm x1i1rx1s x10okhzq xjm9jq1 x132qfvm xb3r6kr x1dordxg x1hyvwdk x10wafsz x47corl xbt4iw xexx8yu x1kw28su"
        },
        children: /*#__PURE__*/_jsx(Button, {
          variant: "secondary",
          label: closeButtonLabel,
          onClick: layer.hide
        })
      })]
    }), {
      ...props,
      xstyle: props?.xstyle
    });
  }, [layer, hasCloseButton, hasSurface, closeButtonLabel, contentRef, dialogLabel, role, isModal, xstyle]);
  return {
    triggerRef,
    contentRef,
    anchorId: layer.anchorId,
    show,
    hide: layer.hide,
    toggle,
    isOpen: layer.isOpen,
    id: layer.id,
    render,
    triggerProps
  };
}